/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-console */
import moment from "moment";
import {
  IHotel28355,
  IRoom40231,
  IRoomPricePerDay,
  IRoomPricePerDayPriceItem,
  IRoomPricingPeriod,
} from "../../common/types";
import firebase from "../../config/firebase";
import { isHotelBirthdayPromoActive } from "../../common/util/hotelBirthdayPromo";
import {
  buildPeriodMapFromPrices,
  calculateRefundableDate,
  calculateRoomTotals,
  computeAvailabilityMeta,
  computeMinStay,
  matchesGuests,
  priceCoversDate,
  selectPreferredPriceItem,
} from "./roomPriceHelpers";
import { db } from "./firestoreService";
import {
  COLLECTION_HOTEL28355,
  COLLECTION_ROOM40231,
  COLLECTION_ROOM_PRICE_PER_DAY,
  COLLECTION_ROOM_PRICE_PERIOD,
} from "./firebaseConstants";

export interface ICheapestRoomForHotel {
  hotel: IHotel28355;
  cheapestRoom: IRoom40231;
  totalPrice: number;
  perNightPrice: number;
  nights: number;
  priceItemsByDate: Array<{
    date: string;
    priceItem: IRoomPricePerDayPriceItem;
    nightPrice: number;
  }>;
}
export interface ICheapestRoomsResult {
  items: ICheapestRoomForHotel[];
  periodsById: Record<string, IRoomPricingPeriod>;
}

export interface IHotelRoomWithDetails extends IRoom40231 {
  hotelId: string;
  prices: IRoomPricePerDay[];
  periods: IRoomPricingPeriod[];
  checkinPeriod?: IRoomPricingPeriod;
  allotment?: number;
  onRequest?: boolean;
  mealsType?: string;
  adults?: number;
  childs?: number;
  infants?: number;
  cancelationPolicy?: string;
  downPaymentAmount?: number | null;
  fullPaymentDueDate?: string | null;
  refundableDate?: string | null;
  partnerId?: string;
  partnerLabel?: string;
  discountPercent?: number;
  stayNights?: number;
  mealsPrices?: Array<{ name: string; price: number }>;
  earlyDiscountPercentage?: number | null;
  earlyDiscountEndDate?: string;
  dayExtrasDescriptions?: string[];
  netPrice?: number;
  totalNetPrice?: number;
  totalPrice: number;
  avgPerNight: number;
  discountPrice: number;
  avgDiscountPerNight: number;
  sellingTotalPrice: number;
  avgSellingPerNight: number;
  hasEarlyBookingDiscount?: boolean;
  minStay?: number;
  hasPositiveAllotment?: boolean;
  allZeroAllotment?: boolean;
  allPricesStopSale?: boolean;
  anyStopSale?: boolean;
  hasOnRequestPeriod?: boolean;
  availableRoomsCount?: number;
  allowPartialPayment?: boolean;
  releaseDate?: string | null;
}

export interface IHotelWithRoomsDetails extends IHotel28355 {
  rooms: IHotelRoomWithDetails[];
}

function chunk<T>(arr: T[], size = 10): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

const isWithinRoomCapacity = (
  room: IRoom40231 & { maxCapacityIncludeInfants?: boolean },
  adults: number,
  children: number,
  infants: number,
) => {
  const maxCapacity = Number(room.maxCapacity);
  const maxAdults = Number(room.maxNumberOfAdults);
  const maxChildren = Number(room.maxNumberOfChildren);
  const maxInfants = Number(room.maxNumberOfInfants);
  const includeInfants = Boolean(room.maxCapacityIncludeInfants);
  const capacityCount = includeInfants
    ? adults + children + infants
    : adults + children;

  if (
    Number.isFinite(maxCapacity) &&
    maxCapacity > 0 &&
    capacityCount > maxCapacity
  ) {
    return false;
  }
  if (Number.isFinite(maxAdults) && maxAdults > 0 && adults > maxAdults) {
    return false;
  }
  if (
    Number.isFinite(maxChildren) &&
    maxChildren > 0 &&
    children > maxChildren
  ) {
    return false;
  }
  if (Number.isFinite(maxInfants) && maxInfants > 0 && infants > maxInfants) {
    return false;
  }
  return true;
};

/**
 * Fetches room price per day docs for a date range and returns
 * hotels with their rooms, attached periods, price docs, and
 * aggregate totals (sum and average per night). Rooms are sorted
 * cheapest-first within each hotel.
 */
export async function getRoomPricesWithDetails(params: {
  fromDate: string;
  toDate: string;
  hotelId?: string;
  adults: number;
  children: number;
  infants: number;
  partnerId?: string;
}): Promise<IHotelWithRoomsDetails[]> {
  const { fromDate, toDate, hotelId, adults, children, infants, partnerId } =
    params;
  const normalizedPartnerId = partnerId
    ? partnerId.charAt(0).toUpperCase() + partnerId.slice(1).toLowerCase()
    : "Poupas";
  const normalizedPartnerIdLower = partnerId ? partnerId.toLowerCase() : "";
  const requestedHotelId = hotelId;
  const nights = Math.max(
    1,
    moment(toDate, "YYYY-MM-DD").diff(moment(fromDate, "YYYY-MM-DD"), "days"),
  );
  const travelDates = Array.from({ length: nights }, (_, index) =>
    moment(fromDate, "YYYY-MM-DD").add(index, "days").format("YYYY-MM-DD"),
  );
  const priceQueryEndDate = travelDates[travelDates.length - 1] || fromDate;

  const roomPrices: IRoomPricePerDay[] = [];
  const hotelIdSet = new Set<string>();
  const roomIdMap = new Map<string, Set<string>>();
  const periodIdSet = new Set<string>();
  const rooms: Array<IRoom40231 & { hotelId: string }> = [];
  if (hotelId) {
    const roomSnap = await db
      .collection(COLLECTION_HOTEL28355)
      .doc(hotelId)
      .collection(COLLECTION_ROOM40231)
      .get();
    const roomIds = new Set<string>();
    roomSnap.forEach((doc) => {
      rooms.push({
        hotelId,
        ...(doc.data() as IRoom40231),
        id: doc.id,
      });
      roomIds.add(doc.id);
    });
    if (!roomIds.size) return [];
    hotelIdSet.add(hotelId);
    roomIdMap.set(hotelId, roomIds);

    const roomIdsWithPrices = new Set<string>();
    await Promise.all(
      Array.from(roomIds).map(async (roomId) => {
        const snap = await db
          .collection(COLLECTION_HOTEL28355)
          .doc(hotelId)
          .collection(COLLECTION_ROOM40231)
          .doc(roomId)
          .collection(COLLECTION_ROOM_PRICE_PER_DAY)
          .where("date", ">=", fromDate)
          .where("date", "<=", priceQueryEndDate)
          .where("status", "==", true)
          .orderBy("date")
          .limit(5000)
          .get();
        snap.forEach((doc) => {
          const data = doc.data() as IRoomPricePerDay;
          if (data.periodId) periodIdSet.add(data.periodId);
          roomPrices.push({ ...data, id: doc.id, hotelId, roomId });
          roomIdsWithPrices.add(roomId);
        });
      }),
    );

    if (!roomPrices.length) return [];
    const eligibleRoomIds = roomIdsWithPrices.size
      ? roomIdsWithPrices
      : roomIds;
    const filteredRooms = rooms.filter((room) => eligibleRoomIds.has(room.id));
    rooms.length = 0;
    rooms.push(...filteredRooms);
  } else {
    const priceQuery = db
      .collectionGroup(COLLECTION_ROOM_PRICE_PER_DAY)
      .where("date", ">=", fromDate)
      .where("date", "<=", priceQueryEndDate)
      .where("status", "==", true)
      .orderBy("date")
      .limit(5000);

    const priceSnap = await priceQuery.get();
    if (priceSnap.empty) return [];

    priceSnap.forEach((doc) => {
      const data = doc.data() as IRoomPricePerDay;
      const pathSegments = doc.ref.path.split("/");
      const hotelId =
        (data as any).hotelId ||
        (pathSegments.length >= 4 ? pathSegments[1] : undefined);
      const roomId =
        (data as any).roomId ||
        (pathSegments.length >= 4 ? pathSegments[3] : undefined);
      if (requestedHotelId && hotelId !== requestedHotelId) {
        return;
      }
      if (hotelId) hotelIdSet.add(hotelId);
      if (hotelId && roomId) {
        if (!roomIdMap.has(hotelId)) roomIdMap.set(hotelId, new Set());
        roomIdMap.get(hotelId)!.add(roomId);
      }
      if (data.periodId) periodIdSet.add(data.periodId);
      roomPrices.push({ ...data, id: doc.id, hotelId, roomId });
    });

    if (!roomPrices.length || !hotelIdSet.size) return [];
  }

  if (!rooms.length) {
    // Load rooms per hotel without collectionGroup
    await Promise.all(
      Array.from(roomIdMap.entries()).map(async ([currentHotelId, roomIds]) => {
        const idChunks = chunk(Array.from(roomIds), 10);
        const snaps = await Promise.all(
          idChunks.map((ids) =>
            db
              .collection(COLLECTION_HOTEL28355)
              .doc(currentHotelId)
              .collection(COLLECTION_ROOM40231)
              .where(firebase.firestore.FieldPath.documentId(), "in", ids)
              .get(),
          ),
        );
        snaps.forEach((snap) => {
          snap.forEach((doc) => {
            rooms.push({
              hotelId: currentHotelId,
              ...(doc.data() as IRoom40231),
              id: doc.id,
            });
            hotelIdSet.add(currentHotelId);
          });
        });
      }),
    );
  }

  const hotelIds = Array.from(hotelIdSet);

  // Load hotels
  const hotelSnaps = await Promise.all(
    chunk(hotelIds, 10).map((ids) =>
      db
        .collection(COLLECTION_HOTEL28355)
        .where(firebase.firestore.FieldPath.documentId(), "in", ids)
        .get(),
    ),
  );
  const hotels: IHotel28355[] = hotelSnaps.flatMap((snap) =>
    snap.docs.map((doc) => ({
      ...(doc.data() as IHotel28355),
      id: doc.id,
    })),
  );

  const filteredHotels = hotelId
    ? hotels.filter((hotel) => hotel.id === hotelId)
    : hotels;

  const periodsById: Record<string, IRoomPricingPeriod> = {};
  const periodIds = Array.from(periodIdSet);
  if (periodIds.length) {
    const periodMap = new Map<string, { hotelId: string; roomId: string }>();
    roomPrices.forEach((price) => {
      if (price.periodId) {
        periodMap.set(price.periodId, {
          hotelId: (price as any).hotelId,
          roomId: (price as any).roomId,
        });
      }
    });

    const groupedByHotelRoom = Array.from(periodMap.entries()).reduce(
      (acc, [pid, info]) => {
        if (!info.hotelId || !info.roomId) return acc;
        const key = `${info.hotelId}__${info.roomId}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(pid);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    await Promise.all(
      Object.entries(groupedByHotelRoom).map(async ([key, pids]) => {
        const [periodHotelId, periodRoomId] = key.split("__");
        const chunks = chunk(pids, 10);
        const snaps = await Promise.all(
          chunks.map((chunkIds) =>
            db
              .collection(COLLECTION_HOTEL28355)
              .doc(periodHotelId)
              .collection(COLLECTION_ROOM40231)
              .doc(periodRoomId)
              .collection(COLLECTION_ROOM_PRICE_PERIOD)
              .where(firebase.firestore.FieldPath.documentId(), "in", chunkIds)
              .get(),
          ),
        );
        snaps.forEach((snap) => {
          snap.forEach((doc) => {
            periodsById[doc.id] = {
              ...(doc.data() as IRoomPricingPeriod),
              id: doc.id,
            };
          });
        });
      }),
    );
  }

  const priceByRoom = roomPrices.reduce(
    (acc, price) => {
      const key = `${(price as any).hotelId || ""}__${(price as any).roomId || ""}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(price);
      return acc;
    },
    {} as Record<string, IRoomPricePerDay[]>,
  );
  const hotelsWithRooms = filteredHotels.map((hotel) => {
    const hotelRooms = rooms
      .filter((r) => r.hotelId === hotel.id)
      .map((room): IHotelRoomWithDetails | null => {
        if (!isWithinRoomCapacity(room as any, adults, children, infants)) {
          return null;
        }
        const prices = priceByRoom[`${room.hotelId}__${room.id}`] || [];
        const { periodMap, periods } = buildPeriodMapFromPrices(
          prices,
          periodsById,
        );
        const checkinPriceDoc = prices.find(
          (p) => p.periodId && priceCoversDate(p, fromDate),
        );
        const checkinPeriod = checkinPriceDoc?.periodId
          ? periodsById[checkinPriceDoc.periodId]
          : undefined;
        const checkinDate = moment(fromDate, "YYYY-MM-DD");
        const releaseDays = Number(checkinPeriod?.releaseDays);
        const releaseDate =
          Number.isFinite(releaseDays) && releaseDays >= 0
            ? checkinDate.clone().subtract(releaseDays, "days")
            : null;
        const refundableDate = calculateRefundableDate(
          checkinDate,
          checkinPeriod?.refundableDays,
        );

        const isReleaseInFuture =
          releaseDate != null && releaseDate.isBefore(moment(), "day");
        const isRefundableDateInFuture =
          refundableDate != null && refundableDate.isBefore(moment(), "day");
        const refundableDateValue = refundableDate
          ? refundableDate.format("YYYY-MM-DD")
          : null;
        const checkinItems = checkinPriceDoc?.priceList || [];
        const matchingCheckinItems = checkinItems.filter((item) =>
          matchesGuests(item, adults, children, infants),
        );
        const selectedPriceItem = selectPreferredPriceItem(
          matchingCheckinItems,
          normalizedPartnerId,
        );
        if (!selectedPriceItem) {
          return null;
        }
        const roomTotals = calculateRoomTotals({
          prices,
          travelDates,
          periodsById,
          adults,
          children,
          infants,
          normalizedPartnerId,
          hotelId: hotel.id,
        });

        // console.log("Calculated totals for room", room.name, {
        //   roomTotals,
        //   prices,
        // });
        let totalPrice = roomTotals.totalPrice;
        let discountTotalPrice = roomTotals.discountTotalPrice;
        let sellingTotalPrice = roomTotals.sellingTotalPrice;
        let totalNetPrice = roomTotals.netTotalPrice;
        const isBookableForAllDates = roomTotals.isBookableForAllDates;
        const { hasOnRequestPeriod } = roomTotals;
        const hasBirthdayPromo = isHotelBirthdayPromoActive({
          partnerId,
          hotelId: hotel.id,
        });
        const promoReferencePrice = roomTotals.totalPrice;

        if (
          hasBirthdayPromo &&
          Number.isFinite(totalPrice) &&
          Number.isFinite(promoReferencePrice) &&
          promoReferencePrice > totalPrice
        ) {
          discountTotalPrice = promoReferencePrice;
        }

        if (!isBookableForAllDates) {
          totalPrice = Number.POSITIVE_INFINITY;
          discountTotalPrice = Number.POSITIVE_INFINITY;
          sellingTotalPrice = Number.POSITIVE_INFINITY;
          totalNetPrice = Number.POSITIVE_INFINITY;
        }

        const minStay = computeMinStay(prices, periodMap, periods);
        const availabilityMeta = computeAvailabilityMeta(prices, travelDates);

        const avgPerNight =
          Number.isFinite(totalPrice) && nights > 0 ? totalPrice / nights : 0;
        const avgDiscountPerNight =
          Number.isFinite(discountTotalPrice) && nights > 0
            ? discountTotalPrice / nights
            : 0;
        const avgSellingPerNight =
          Number.isFinite(sellingTotalPrice) && nights > 0
            ? sellingTotalPrice / nights
            : 0;
        const lowestAllotment = prices.reduce((min, price) => {
          const value = Math.max(
            0,
            Number(price?.roomAllotment ?? 0) - Number(price?.allotment ?? 0),
          );
          if (!Number.isFinite(value)) return min;
          return Math.min(min, value);
        }, Number.POSITIVE_INFINITY);
        const mealsTotals = travelDates.reduce(
          (acc, date) => {
            const matchingDoc = prices.find(
              (p) =>
                priceCoversDate(p, date) && !p.stopSale && p.status !== false,
              // (p.roomAllotment || 0) > (p.allotment || 0),
            );
            if (!matchingDoc) return acc;
            const matchingItems = (matchingDoc.priceList || []).filter(
              (item) => {
                const matchesAdults = Number(item.adults ?? 0) === +adults;
                const matchesChildren = Number(item.childs ?? 0) === +children;
                const infantsValue = (item as any).infants;
                const matchesInfants =
                  infantsValue == null || Number(infantsValue) >= +infants;
                return matchesAdults && matchesChildren && matchesInfants;
              },
            );
            const itemToUse =
              (selectedPriceItem?.id
                ? matchingItems.find((item) => item.id === selectedPriceItem.id)
                : undefined) || matchingItems[0];
            (itemToUse?.mealsPrices || []).forEach((meal) => {
              acc[meal.name] = (acc[meal.name] || 0) + (+meal.price || 0);
            });
            return acc;
          },
          {} as Record<string, number>,
        );
        const mealsPrices = Object.entries(mealsTotals).map(
          ([name, price]) => ({ name, price }),
        );
        const dayExtrasDescriptions = Array.from(
          new Set(
            prices.flatMap((price) =>
              (price.priceList || [])
                .filter((item) =>
                  matchesGuests(item, adults, children, infants),
                )
                .flatMap((item) => item.dayExtrasDescriptions || []),
            ),
          ),
        );

        const earlyDiscountPercentage =
          checkinPeriod?.earlyDiscountPercentage ?? null;
        const earlyDiscountEndDate = checkinPeriod?.earlyDiscountEndDate;
        const earlyDiscountEndMoment = earlyDiscountEndDate
          ? moment(earlyDiscountEndDate, "YYYY-MM-DD", true)
          : null;
        const hasActiveEarlyBookingDiscount =
          Number.isFinite(Number(earlyDiscountPercentage)) &&
          Number(earlyDiscountPercentage) > 0 &&
          !!earlyDiscountEndMoment &&
          earlyDiscountEndMoment.isValid() &&
          (moment().isBefore(earlyDiscountEndMoment, "day") ||
            moment().isSame(earlyDiscountEndMoment, "day"));
        // console.log("checkinPeriod", checkinPeriod, "room", room, "hotel", hotel);
        return {
          ...room,
          prices,
          periods,
          mealsType: selectedPriceItem?.mealsType ?? checkinPeriod?.mealsType,
          adults: selectedPriceItem?.adults,
          childs: selectedPriceItem?.childs,
          infants: selectedPriceItem?.infants,
          // netPrice: selectedPriceItem?.netPrice,
          cancelationPolicy: isRefundableDateInFuture
            ? "NON_REFUNDABLE"
            : selectedPriceItem?.cancelationPolicy,
          downPaymentAmount: selectedPriceItem?.downPaymentAmount ?? null,
          fullPaymentDueDate: selectedPriceItem?.fullPaymentDueDate ?? null,
          refundableDate:
            refundableDateValue ?? selectedPriceItem?.refundableDate ?? null,
          partnerId: selectedPriceItem?.partnerId,
          partnerLabel: selectedPriceItem?.partnerLabel,
          discountPercent: selectedPriceItem?.discountPercent,
          stayNights: selectedPriceItem?.stayNights,
          mealsPrices,
          dayExtrasDescriptions,
          totalNetPrice,
          allotment: Number.isFinite(lowestAllotment)
            ? lowestAllotment
            : undefined,
          onRequest: checkinPeriod?.onRequest || isReleaseInFuture,
          earlyDiscountPercentage,
          earlyDiscountEndDate,
          totalPrice,
          avgPerNight,
          discountPrice: discountTotalPrice,
          avgDiscountPerNight,
          sellingTotalPrice,
          avgSellingPerNight,
          hasEarlyBookingDiscount: hasActiveEarlyBookingDiscount,
          minStay,
          hasPositiveAllotment: availabilityMeta.hasPositiveAllotment,
          allZeroAllotment: availabilityMeta.allZeroAllotment,
          allPricesStopSale: availabilityMeta.allPricesStopSale,
          anyStopSale: availabilityMeta.anyStopSale,
          hasOnRequestPeriod,
          availableRoomsCount: availabilityMeta.availableRoomsCount,
          allowPartialPayment: isRefundableDateInFuture ? false : undefined,
        };
      })
      .filter((room): room is IHotelRoomWithDetails => room !== null)
      .sort((a, b) => a.totalPrice - b.totalPrice);
    const partnerFilteredRooms = partnerId
      ? hotelRooms.filter((room) =>
          room.periods.every(
            (period) =>
              Array.isArray(period.partnerId) &&
              period.partnerId.some(
                (id) =>
                  typeof id === "string" &&
                  id.toLowerCase() === normalizedPartnerIdLower,
              ),
          ),
        )
      : hotelRooms;
    // console.log("hotelRooms", {
    //   hotelName: hotel.name,
    //   rooms: hotelRooms,
    //   partnerFilteredRooms,
    //   partnerId,
    // });
    if (!partnerFilteredRooms.length) {
      return null;
    }
    // console.log("partnerFilteredRooms", {
    //   ...hotel,
    //   rooms: partnerFilteredRooms,
    // });
    return { ...hotel, rooms: partnerFilteredRooms };
  });
  return hotelsWithRooms.filter(
    (hotel): hotel is IHotelWithRoomsDetails => hotel !== null,
  );
}
